import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";

import About from "./pages/About";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import TemplateGallery from "./pages/TemplateGallery";
import TemplateBuilder from "./pages/TemplateBuilder";
// Import CampaignBuilder with default export
import CampaignBuilder from "./pages/CampaignBuilder";
import TemplatePreview from "./pages/Templatepreview";
import FlyerEditor from "./pages/FlyerEditor";
import AdminUsers from "./pages/AdminUsers";
import AdminAgents from "./pages/AdminAgents";
import FlyerControl from "./pages/FlyerControl";
import PropertyImport from "./pages/PropertyImport";
import CampaignManagement from "./pages/CampaignManagement";
import Analytics from "./pages/Analytics";
import TemplateManagement from "./pages/TemplateManagement";
import AccountSettings from "./pages/AccountSettings";
import Help from "./pages/Help";
import NotFound from "./pages/NotFound";
import ForgotPassword from "./pages/ForgotPassword";
import NewPassword from "./pages/NewPassword";
import SuccessPage from "./pages/PaymentSuccess";
import PaymentFailure from "./pages/PaymentFailure";
import ScrollToTop from "./components/ScrollToTop";
import PageTitle from "./components/PageTitle";
import TermsOfService from "@/components/TermsAndConditions";
import FlyerTemplate from "./components/templates/FlyerTemplate";
import Template1 from "@/components/templates/Template1";
import Template2 from "./components/templates/Template2";
import Template3 from "./components/templates/Template3";
import Template4 from "@/components/templates/Template4";
import Template5 from "@/components/templates/Template5";
import Template6 from "@/components/templates/Template6";
import Template7 from "@/components/templates/Template7";
import Template8 from "@/components/templates/Template8";
import Template9 from "@/components/templates/Template9";
import Template10 from "./components/templates/Template10";
import Template11 from "./components/templates/Template11";
import MultiTemplate from "@/components/templates/MultiTemplate";
import CampaignEdit from "./pages/CampaignEdit";
import CampaignView from "./pages/CampaignView";
import AddOnSuccess from "./pages/AddOnSuccess";
import Contact from "./components/Contact";
import ServiceCampaignBuilder from "./pages/ServiceCampaign";
import ContactInfoStep from "./pages/AgentDataSet";
import SocialMediaUploads from "./pages/SocialMediaUploads";
import ServiceTemplate1 from "./components/ServiceTemplate/ServiceTemplate1";
import RefundPolicy from "./components/RefundPolicy";
// === NEW IMPORT ===
import BetaChecklist from "./pages/BetaChecklist";
import BetaAgreement from "./pages/BetaAgreement";
import DraftPage from "./components/DraftDialog";
import StripeProvider from "./components/StripeProvider";
import ReviewPage from "./pages/Review";
import ReplyToSender  from "./pages/ReplyToSender";
import { lazy, Suspense } from "react";

// CMA pages are loaded on demand so they add nothing to the initial bundle.
const CMA = lazy(() => import("./pages/CMA"));
const CMADetail = lazy(() => import("./pages/CMADetail"));
const CMAFallback = <div className="min-h-screen bg-white" />;

const queryClient = new QueryClient();

const RedirectToBackend: React.FC<{url: string}> = ({ url }) => {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);
  return null;
};

const LaunchesRouteRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <Navigate to={`/launches-view/${id}`} replace /> : <Navigate to="/launches" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <StripeProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PageTitle />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/failure" element={<PaymentFailure />} />
          <Route path="/about" element={<About />} />
          {/* redirect placeholders so Django handles these routes */}
          <Route path="/terms-and-conditions" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/set-password/:uidb64/:token" element={<NewPassword />} />
          <Route path="/broker" element={<ServiceTemplate1 />} />

          {/* Public routes */}
          <Route element={<PublicRoute />}>
            <Route path="/signup" element={<Login />} />
          </Route>

          {/* === NEW PUBLIC ROUTE: Beta Checklist === */}
          <Route path="/beta-checklist" element={<BetaChecklist />} />
          <Route path="/beta-agreement" element={<BetaAgreement />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/templates" element={<TemplateGallery />} />
            <Route path="/template-builder" element={<TemplateBuilder />} />
            <Route path="/template-preview/:id" element={<TemplatePreview />} />
            <Route path="/flyer-editor/:templateId" element={<FlyerEditor />} />
            <Route path="/builder/:templateId" element={<CampaignBuilder />} />
            <Route path="/addons/success" element={<AddOnSuccess />} />
            <Route path="/builder" element={<CampaignBuilder />} />
            <Route path="/service-campaign" element={<ServiceCampaignBuilder />} />
            <Route path="/agent" element={<ContactInfoStep />} />
            <Route path="/propertyimport" element={<PropertyImport />} />
            <Route path="/launches" element={<CampaignManagement />} />
            <Route path="/launches/:id" element={<LaunchesRouteRedirect />} />
            <Route path="/launches-edit/:id" element={<CampaignEdit />} />
            <Route path="/launches-view/:id" element={<CampaignView />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/cma" element={<Suspense fallback={CMAFallback}><CMA /></Suspense>} />
            <Route path="/cma/:id" element={<Suspense fallback={CMAFallback}><CMADetail /></Suspense>} />
            <Route path="/social-media" element={<SocialMediaUploads />} />
            <Route path="/saved-templates" element={<TemplateManagement />} />
            <Route path="/settings" element={<AccountSettings />} />
            <Route path="/help" element={<Help />} />
            <Route path="/manage/users" element={<AdminUsers />} />
            <Route path="/manage/agents" element={<AdminAgents />} />
            <Route path="/manage/flyer-control" element={<FlyerControl />} />
            <Route path="/draft-campaign" element={<DraftPage />} />
          </Route>

          {/* Template preview routes */}
          <Route
            path="/templates/Template1"
            element={
              <FlyerTemplate
                view={Template1}
                propertyLists={[]}
                keyFeatures={[]}
              />
            }
          />
          <Route path="/templates/Template2" element={<Template2 />} />
          <Route path="/templates/Template3" element={<Template3 />} />
          <Route path="/templates/Template4" element={<Template4 />} />
          <Route path="/templates/Template5" element={<Template5 />} />
          <Route path="/templates/Template6" element={<Template6 />} />
          <Route path="/templates/Template7" element={<Template7 />} />
          <Route path="/templates/Template8" element={<Template8 />} />
          <Route path="/templates/Template9" element={<Template9 />} />
          <Route path="/templates/Template10" element={<Template10 />} />
          <Route path="/templates/Template11" element={<Template11 />} />
          <Route path="/templates/MultiTemplate" element={<MultiTemplate />} />

          <Route path="/review" element={<ReviewPage />} />
         <Route path="/reply-to-sender/:campaignId/:agentId" element={<ReplyToSender />} />

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      </StripeProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
